import type { TileOf } from '@casa/model';
import { DEFAULT_VALUE_COLOR, formatTileNumber } from '../value/value_tile.ts';

/**
 * The `text` tile (docs/specs/text-tile.md, issue #22): one text, the tile's own or its source's
 * value, in the tile's text colour, at a size in pixels, aligned both ways, wrapped or not.
 */

export type TextTile = TileOf<'text'>;

export interface TextTileContent {
    text: string;
    color: string;
    size: number;
    align: 'left' | 'center' | 'right';
    valign: 'top' | 'center' | 'bottom';
    wrap: boolean;
}

/** Whether the tile reads its source: where it has a data source by that name (TX-2). */
export function readsSource(tile: TextTile) : boolean {
    return tile.dataSources[tile.options.source] !== undefined;
}

/** A source's value as a text (TX-2): "-" for none, a number in up to two decimals, on and off in words. */
export function valueAsText(value: unknown, locale: string | undefined) : string {
    if (value === null || value === undefined) {
        return '-';
    }
    if (typeof value === 'number') {
        return formatTileNumber(value, '0.##', locale);
    }
    if (typeof value === 'boolean') {
        return value ? 'On' : 'Off';
    }

    return String(value);
}

/** What the tile shows, given its source's last value (`undefined` where it reads none). */
export function textTileContent(tile: TextTile, value: unknown, locale: string | undefined, textColor = DEFAULT_VALUE_COLOR) : TextTileContent {
    const { text, unit, size, align, valign, wrap } = tile.options;
    // The unit after a reading's number only (TX-7): not after words, nor "-" for none.
    const shown = readsSource(tile) ? `${valueAsText(value, locale)}${typeof value === 'number' && unit !== '' ? ` ${unit}` : ''}` : text;

    return { text: shown, color: textColor, size, align, valign, wrap };
}
